'use client';

import { useMemo } from 'react';
import type { LobbyGridPage } from '@/hooks/use-lobby-grid-page';
import {
  adaptersForBoard,
  type LobbyAdapter,
} from '@/features/lobby/data/mock-adapters';
import { gridAssetsToAdapters } from '@/features/lobby/data/grid-to-adapters';
import { resolveLobbyLiveGridId } from '@/features/lobby/data/boardroom-grid';
import type { GridAdapterNote } from '@/lib/types/v2';
import { useTutorialOptional } from '@/features/tutorial/TutorialContext';
import {
  TUTORIAL_BOARD_ID,
  buildTutorialAdapters,
} from '@/features/tutorial/tutorial-board';
import { useTakeoverPrice } from '@/hooks/use-takeover-price';

export interface UseLobbyAdaptersOpts {
  boardId: string;
  /** Display name used by the mock fallback when no adapters are
   *  registered against the board id. */
  fallbackName: string;
  /** Live grid page result hoisted from `useLobbyGridPage(activeBoardId)`
   *  in the parent so the adapters/seats/rewards hooks share one query
   *  + one SSE stream. See `use-lobby-grid-page.ts` for the rationale. */
  gridPage: LobbyGridPage;
  /** Merged from `GET /api/grid/...` (`pool_metadata.adapter_notes`). */
  adapterNotes?: Record<string, GridAdapterNote>;
}

export interface UseLobbyAdaptersResult {
  adapters: LobbyAdapter[];
  /** True while the live grid is mid-fetch. Mock-backed boards are
   *  always considered loaded. Consumers can show a placeholder /
   *  shimmer state instead of an empty stack. */
  isLoading: boolean;
  /** True when adapters are derived from a live grid asset list rather
   *  than the mock registry. Useful for any UI that wants to badge
   *  "live yield" vs "demo data". */
  isLive: boolean;
}

/**
 * Resolve the adapter list to render in the lobby's per-board panel
 * stack. For a live V2 grid id we read `assets` off the shared
 * `useLobbyGridPage` result that powers seats / floor / activity, then
 * map each asset into its discriminated panel variant via
 * `gridAssetsToAdapters`. Non-live boards (boardroom snapshot, dev-only
 * mocks) fall through to the existing `adaptersForBoard(boardId)`
 * registry so nothing in the lobby blanks out while live data is being
 * wired in.
 */
export function useLobbyAdapters(opts: UseLobbyAdaptersOpts): UseLobbyAdaptersResult {
  const { boardId, fallbackName, gridPage, adapterNotes } = opts;
  const tutorial = useTutorialOptional();
  // Boardroom translates to HV grid 0 here; live community ids pass
  // through unchanged; mock-only ids resolve to `null` and fall back
  // to the curated adapter registry.
  const liveId = resolveLobbyLiveGridId(boardId);
  const { assets, gridLoading } = gridPage;
  // Live TAKEOVER/USD rate threaded into the mock-adapter fallback so
  // the Boardroom placeholder card stops tripping the `mock-prices`
  // dev warning. Live grids ignore this — `gridAssetsToAdapters`
  // formats USD off the asset payload directly.
  const { priceUsd: takeoverPriceUsd } = useTakeoverPrice();

  return useMemo<UseLobbyAdaptersResult>(() => {
    // Tutorial mode: a fixed list of dummy adapters powers the yield panel
    // narration without touching the real registry.
    if (tutorial && boardId === TUTORIAL_BOARD_ID) {
      return {
        adapters: buildTutorialAdapters(),
        isLoading: false,
        isLive: false,
      };
    }
    if (liveId) {
      const adapters = gridAssetsToAdapters(assets ?? [], { adapterNotes });
      return {
        adapters,
        isLoading: gridLoading,
        isLive: true,
      };
    }
    return {
      adapters: adaptersForBoard(boardId, fallbackName, takeoverPriceUsd),
      isLoading: false,
      isLive: false,
    };
  }, [tutorial, liveId, assets, gridLoading, boardId, fallbackName, adapterNotes, takeoverPriceUsd]);
}
